<template>
  <!-- LINHA DA TABELA -->
  <div
    :class="{
      'datatable--row__selected-color': selectedBgColor
    }"
    class="tr-wrapper"
    @click.stop.prevent="selectRow(!rowData.data.checked)"
  >
    <!-- LINHA PRINCIPAL -->
    <div class="row tr no-gutters">
      <!-- COLUNA DE SELEÇÃO -->
      <div
        v-if="selectableRow"
        :key="`datatable-${componentKey}-checkbox-item`"
        class="col td td--selection"
      >
        <InputCheckbox
          :checked="rowData.data.checked"
          class="datatable--checkbox_color"
          @change="selectRow"
        />
      </div>
      <!-- / COLUNA DE SELEÇÃO -->

      <!-- COLUNA DA TABELA -->
      <div
        v-for="(key, i) in cols"
        :key="`datatable-${componentKey}-${key.name}-${i}`"
        :style="{
          'min-width': `${colsMinWidths ? colsMinWidths[key.name] : ''}`
        }"
        class="col td"
      >
        <button
          v-if="i == 0 && hasDetails"
          class="btn-show-details"
          :class="{ opened: showDetails }"
          @click="showDetails = !showDetails"
        >
          <i class="icon icon-sort-down icon-12"></i>
        </button>
        <slot
          :name="key.name"
          :col="rowData.data[key.name]"
          :rowDataObj="rowData.data"
        >
          <span :title="formatedValue(key)" v-html="formatedValue(key)"></span>
        </slot>
      </div>
    </div>
    <!-- LINHA DE DETALHES -->
    <div class="details">
      <!-- COLUNA DA TABELA -->
      <template v-if="showDetails && hasDetails">
        <slot :name="`details`" :detailsData="rowData.details">
          <DatatableRow
            v-for="(entry, idx) in rowData.details"
            :key="JSON.stringify(entry)"
            :rowData="entry"
            :cols="cols"
            :row-index="idx"
          />
        </slot>
      </template>
    </div>
  </div>
</template>

<script>
import InputCheckbox from '../elements/InputCheckbox'
import { getHighlightedText } from '@/helpers/string'

export default {
  name: 'DatatableRow',
  components: {
    InputCheckbox
  },
  props: {
    /**
     * Dado que popula a linha
     * {
     *  data: {
     *    ...
     *  }
     * }
     */
    rowData: {
      type: Object,
      default: () => {}
    },
    cols: {
      type: Array,
      default: () => []
    },
    rowIndex: {
      type: Number,
      required: true
    },
    colsMinWidths: {
      type: Object,
      default: () => {}
    },
    noFrames: {
      type: Boolean,
      default: false
    },
    selectableRow: {
      type: Boolean,
      default: false
    },
    /**
     * Termo buscado e destacado na linha
     */
    filterKey: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      componentKey: new Date().getTime(),
      showDetails: false
    }
  },
  computed: {
    hasDetails() {
      return this.rowData?.details?.length > 0
    },
    selectedBgColor() {
      return this.selectableRow && this.noFrames && this.rowData.data.checked
    }
  },
  created() {
    /** Se não existe o atributo checked, adiciona */
    if (!this.rowData.data.checked) {
      this.$set(this.rowData.data, 'checked', false)
    }
  },
  methods: {
    formatedValue(key) {
      if (
        this.rowData.data[key.name] &&
        !Number.isNaN(+this.rowData.data[key.name])
      ) {
        return this.$options.filters.numFormat(this.rowData.data[key.name])
      }

      if (this.filterKey.trim() !== '') {
        return (
          getHighlightedText(this.filterKey, this.rowData.data[key.name]) || '-'
        )
      }

      return this.rowData.data[key.name] || '-'
    },
    selectRow(value) {
      this.rowData.data.checked = value
      this.$emit('checkRow', value)
      this.$emit('rowInteraction', this.rowData.data)
    }
  }
}
</script>

<style lang="scss" scoped>
$head-color: $gray-darkest;
$border-color: $gray-border;
$bg-row-stripe: $gray-lightest;

.btn-show-details {
  position: relative;
  padding: 0;
  width: 15px;
  height: 15px;
  line-height: 1;
  border: none;
  background: none;
  color: #7f7f7f;
  outline: none;

  i {
    position: absolute;
    transform: rotateZ(-90deg) translate(50%, -50%);
    top: 50%;
    left: 50%;
    transform-origin: center center;
  }

  &.opened {
    i {
      transform: rotateZ(0) translate(-50%, -50%);
    }
  }
}

.row {
  &.tr {
    flex-wrap: nowrap;
  }
}

.td {
  display: flex;
  align-items: center;
  font-size: 11px;
  padding: 10px 10px;
  flex: 1 1 auto;
  min-width: 100px;
  box-sizing: border-box;
  border: 1px solid $border-color;
  border-top: none;
  border-left: none;
  span {
    @include line-overflow(11px, 11px, 1);
  }

  &:last-child {
    border-right: none;
  }

  &--selection {
    max-width: 48px;
    min-width: 48px;
  }
}

.tr-wrapper {
  flex: 1 0 100%;

  &:nth-child(odd) {
    background-color: $bg-row-stripe;
    .td {
      background-color: inherit;
    }
  }

  &:last-child {
    .td {
      border-bottom: none;
    }
  }
}

/** MODELO SEM LINHAS DIVISÓRIAS */
.no-frames {
  .td {
    border: none;
    border-top: 1px solid $gray-border;
    justify-content: center;
    text-align: center;
    padding: 12px 14px;
    font-size: font-size('size-medium');

    span {
      @include line-overflow(14px, 14px, 1);
    }

    &:first-child {
      justify-content: flex-start;
      text-align: left;
    }
  }

  .tr-wrapper {
    background-color: transparent;
    transition: background-color $color-anim;

    &:nth-child(odd) {
      background-color: transparent;
    }
  }

  &.has-row-selection {
    .td {
      &:nth-child(2) {
        justify-content: flex-start;
        text-align: left;
      }
    }
  }

  .datatable {
    &--checkbox_color {
      ::v-deep {
        input {
          &:checked ~ .input-checkbox--fakebox {
            background-color: $white;

            .icon {
              color: $primary;
            }
          }
        }
      }
    }

    &--row {
      &__selected-color {
        &,
        &:nth-child(odd) {
          background-color: $primary;

          .td {
            span {
              color: $white;
            }
          }
        }
      }
    }
  }
}
</style>
